import BigNumber from "bignumber.js";

export type ChainMeta = Record<string, unknown> | null | undefined;

/** EVM chain label / name → EIP-155 chain id (best-effort; API may add explicit ids later). */
const CHAIN_LABEL_TO_EVM_ID: ReadonlyArray<{ match: RegExp; chainId: number }> =
  [
    { match: /^(ethereum|eth|mainnet)$/i, chainId: 1 },
    { match: /polygon|matic/i, chainId: 137 },
    { match: /arbitrum|arb(?!it)/i, chainId: 42161 },
    { match: /optimism|\bop\b/i, chainId: 10 },
    { match: /base\b/i, chainId: 8453 },
    { match: /bsc|binance|bnb\s*smart/i, chainId: 56 },
    { match: /avalanche|avax|c-?chain/i, chainId: 43114 },
    { match: /fantom|\bftm\b/i, chainId: 250 },
    { match: /linea/i, chainId: 59144 },
    { match: /celo/i, chainId: 42220 },
    { match: /gnosis|xdai/i, chainId: 100 },
    { match: /scroll/i, chainId: 534352 },
    { match: /zksync|zk\s*era/i, chainId: 324 },
    { match: /blast/i, chainId: 81457 },
  ];

/** Native gas token symbols per chain for optional `value` (wei) in EIP-681. */
const NATIVE_SYMBOLS_BY_CHAIN_MAP: ReadonlyMap<number, readonly string[]> =
  new Map([
    [1, ["ETH"]],
    [137, ["MATIC", "POL"]],
    [56, ["BNB", "BSC"]],
    [43114, ["AVAX"]],
    [42161, ["ETH"]],
    [10, ["ETH"]],
    [8453, ["ETH"]],
    [250, ["FTM"]],
    [42220, ["CELO"]],
    [100, ["XDAI", "DAI"]],
    [59144, ["ETH"]],
    [534352, ["ETH"]],
    [324, ["ETH"]],
    [81457, ["ETH"]],
  ]);

const EVM_ADDRESS = /^0x[a-fA-F0-9]{40}$/;
const BTC_LIKE = /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,87}$/;
const LTC_LIKE = /^(ltc1|[LM3])[a-zA-HJ-NP-Z0-9]{25,90}$/;
/** Base58 Solana address length is typically 32–44 chars. */
const SOL_LIKE = /^[1-9A-HJ-NP-Za-km-z]{32,48}$/;

function readNumericCandidate(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) {
    return raw;
  }
  if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    const n = parseInt(raw, 10);
    if (n > 0) {
      return n;
    }
  }
  return null;
}

function readNumericChainId(chain: ChainMeta): number | null {
  if (!chain || typeof chain !== "object") {
    return null;
  }
  const c = chain as {
    evmChainId?: unknown;
    chainId?: unknown;
    nativeChainId?: unknown;
    eip155ChainId?: unknown;
    numericChainId?: unknown;
  };
  return (
    readNumericCandidate(c.evmChainId) ??
    readNumericCandidate(c.chainId) ??
    readNumericCandidate(c.nativeChainId) ??
    readNumericCandidate(c.eip155ChainId) ??
    readNumericCandidate(c.numericChainId)
  );
}

export function inferEvmChainId(
  chainLabel: string | null | undefined,
  chain?: ChainMeta
): number | null {
  const fromMeta = readNumericChainId(chain);
  if (fromMeta != null) {
    return fromMeta;
  }
  const label = (chainLabel || "").trim();
  if (!label) {
    return null;
  }
  for (const { match, chainId } of CHAIN_LABEL_TO_EVM_ID) {
    if (match.test(label)) {
      return chainId;
    }
  }
  return null;
}

function isNativeTokenForChain(
  chainId: number,
  assetSymbol: string | null | undefined
): boolean {
  if (!assetSymbol) {
    return false;
  }
  const upper = assetSymbol.trim().toUpperCase();
  const natives = NATIVE_SYMBOLS_BY_CHAIN_MAP.get(chainId);
  if (!natives) {
    return false;
  }
  return natives.includes(upper);
}

function toWeiString(amountDecimal: string, decimals: number): string | null {
  const trimmed = amountDecimal.trim();
  if (!trimmed || !/^[\d.]+$/.test(trimmed)) {
    return null;
  }
  try {
    const wei = new BigNumber(trimmed).times(new BigNumber(10).pow(decimals));
    if (!wei.isFinite() || wei.isNegative()) {
      return null;
    }
    return wei.integerValue(BigNumber.ROUND_FLOOR).toFixed(0);
  } catch {
    return null;
  }
}

export interface BuildUniversalWalletPaymentUriInput {
  address: string;
  assetSymbol?: string | null;
  chainLabel?: string | null;
  chain?: ChainMeta;
  /** Human-readable crypto amount (optional; only for native gas tokens). */
  amountCrypto?: string | null;
}

/**
 * Builds a wallet-interoperable payment URI (EIP-681-style for EVM, BIP-21 for Bitcoin, etc.).
 * Omits sensitive fields; optional memo/reference should be added only when product requires it.
 */
export function buildUniversalWalletPaymentUri(
  input: BuildUniversalWalletPaymentUriInput
): string | null {
  const raw = (input.address || "").trim();
  if (!raw) {
    return null;
  }

  const asset = (input.assetSymbol || "").trim().toUpperCase();
  const chainLabel = input.chainLabel?.trim() || "";

  if (EVM_ADDRESS.test(raw)) {
    const chainId = inferEvmChainId(chainLabel || null, input.chain);
    let path = `ethereum:${raw}`;
    if (chainId != null) {
      path = `${path}@${chainId}`;
    }
    if (
      chainId != null &&
      input.amountCrypto &&
      isNativeTokenForChain(chainId, asset)
    ) {
      const wei = toWeiString(input.amountCrypto, 18);
      if (wei && !wei.startsWith("-")) {
        path = `${path}?value=${wei}`;
      }
    }
    return path;
  }

  if ((asset === "BTC" || !asset) && BTC_LIKE.test(raw)) {
    let u = `bitcoin:${raw}`;
    if (input.amountCrypto) {
      const a = input.amountCrypto.trim();
      if (/^[\d.]+$/.test(a)) {
        u += `?amount=${encodeURIComponent(a)}`;
      }
    }
    return u;
  }

  if ((asset === "LTC" || !asset) && LTC_LIKE.test(raw)) {
    return `litecoin:${raw}`;
  }

  if (asset === "SOL" || SOL_LIKE.test(raw)) {
    return `solana:${raw}`;
  }

  if (SOL_LIKE.test(raw) && raw.length >= 32) {
    return `solana:${raw}`;
  }

  return null;
}

/**
 * True when the string already looks like a structured payment / deep link (not a bare address).
 */
export function isLikelyUniversalPaymentUri(
  value: string | null | undefined
): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const v = value.trim();
  if (!v) {
    return false;
  }
  if (/^https?:\/\//i.test(v)) {
    return true;
  }
  if (/^(ethereum|bitcoin|litecoin|solana|wc:|wallet_|eightyeight:)/i.test(v)) {
    return true;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(v) && !EVM_ADDRESS.test(v)) {
    return true;
  }
  return false;
}

/**
 * Prefers a backend-provided URI when it is already universal; otherwise uses a client-built URI.
 */
export function preferUniversalPaymentUri(
  backendQr: string | null | undefined,
  clientUri: string | null | undefined
): string | null {
  const b = typeof backendQr === "string" ? backendQr.trim() : "";
  const c = typeof clientUri === "string" ? clientUri.trim() : "";

  if (b && isLikelyUniversalPaymentUri(b)) {
    return b;
  }
  if (c) {
    return c;
  }
  if (b) {
    return b;
  }
  return null;
}
